import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../common/types';
import { CatalogItem, ReleaseKitSummary } from '../../../../redux/api/fymAPI.types';
import { useGetReleaseKitsQuery } from '../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useOpenAddSong } from '../../../smartLinks/catalog/addSong/useOpenAddSong';
import { useOpenSongPage } from '../../../smartLinks/catalog/song/page/useOpenSongPage';
import { useKitWebFonts } from '../themes/kitWebFonts';
import { studioMessages } from './studioMessages';
import { groupSongsForPicker } from './picker/groupSongsForPicker';
import { songPickerMessages as m } from './picker/songPickerMessages';
import { SongPickerCard } from './picker/SongPickerCard';
import { SongPickerAddCard } from './picker/SongPickerAddCard';
import { SongPickerGroup } from './picker/SongPickerGroup';
import { StudioProfileEntryCard } from '../profile/entry/StudioProfileEntryCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './picker/SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** "Find a song" shows from this many songs (design t1). */
export const PICKER_SEARCH_FROM = 9;

interface StudioSongPickerProps {
  accountId: number;
  items: CatalogItem[];
  /** "Make something" and the hero: no asset type. A Next up chip: its asset type. */
  onPick: (catalogItemUuid: string, assetTypeId?: StudioThemeId) => void;
}

/**
 * The Studio with no song (design release-kit t1, t1b): the songs as cards in their release theme,
 * in two groups, "Coming out" (the nearest first) and "Out now" (the newest first). The dashed card
 * at the end of "Coming out" opens the add-unreleased modal. "Find a song" filters by title, from
 * 9 songs. The empty catalog is `StudioNoSongs` (t1c). The "Your profile" card (design
 * artist-profile c3) sits above the groups.
 */
const StudioSongPickerComponent: React.FC<StudioSongPickerProps> = ({ accountId, items, onPick }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  useKitWebFonts();
  const [query, setQuery] = useState('');
  const { data: kits } = useGetReleaseKitsQuery({ accountId });
  const openCatalogSong = useOpenSongPage();
  // "Add the master" opens Song files of the song page (design song-page c2).
  const openSongFiles = useCallback((row: CatalogItem) => openCatalogSong(row.uuid, 'files'), [openCatalogSong]);

  const summaries = useMemo(
    () => new Map<string, ReleaseKitSummary>((kits?.kits ?? []).map((kit) => [kit.catalogItemUuid, kit])),
    [kits]
  );
  const now = useMemo(() => new Date(), []);
  const showSearch = items.length >= PICKER_SEARCH_FROM;
  const needle = showSearch ? query.trim().toLocaleLowerCase() : '';
  const groups = useMemo(() => {
    const shown = needle === '' ? items : items.filter((item) => item.title.toLocaleLowerCase().includes(needle));
    return groupSongsForPicker(shown, now);
  }, [items, needle, now]);

  const openSongPage = useCallback(
    (catalogItemUuid: string) => router.push(SMLRoutes.promoteRelease, { catalogItemUuid }),
    [router]
  );
  // design catalog-add-song: the add card opens the step-by-step page; Close comes back to the Studio.
  const openUnreleased = useOpenAddSong('studio', accountId);

  const card = (item: CatalogItem) => (
    <SongPickerCard
      key={item.uuid}
      item={item}
      summary={summaries.get(item.uuid)}
      now={now}
      onPick={onPick}
      onAddMaster={openSongFiles}
      onOpenSongPage={openSongPage}
    />
  );
  const noMatch = needle !== '' && groups.comingOut.length === 0 && groups.outNow.length === 0;

  return (
    <div className="d-flex flex-column gap-4">
      <div className={styles.head}>
        <div className={styles.headText}>
          <h2 className={styles.title}>{intl.formatMessage(studioMessages.pickTitle)}</h2>
          <span className={styles.sub}>{intl.formatMessage(m.subtitle)}</span>
        </div>
        {showSearch && (
          <input
            type="search"
            className={styles.find}
            placeholder={intl.formatMessage(m.find)}
            aria-label={intl.formatMessage(m.find)}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        )}
      </div>
      {/* design artist-profile c3: the Profile of the artist, above the song groups. */}
      <StudioProfileEntryCard accountId={accountId} />
      {noMatch && <p className="text-muted small mb-0">{intl.formatMessage(m.noMatch, { query: query.trim() })}</p>}
      <SongPickerGroup
        title={intl.formatMessage(m.groupComingOut)}
        sub={intl.formatMessage(m.groupComingOutSub)}
        testId="song-picker-coming-out"
      >
        {groups.comingOut.map(card)}
        <SongPickerAddCard onClick={openUnreleased} />
      </SongPickerGroup>
      {groups.outNow.length > 0 && (
        <SongPickerGroup
          title={intl.formatMessage(m.groupOutNow)}
          sub={intl.formatMessage(m.groupOutNowSub)}
          testId="song-picker-out-now"
        >
          {groups.outNow.map(card)}
        </SongPickerGroup>
      )}
    </div>
  );
};

export const StudioSongPicker = TypedMemo(StudioSongPickerComponent);
